<template>
  <v-container fluid>
    <v-row>
      <v-col>
        <span class="d-flex flex-row align-center">
          <v-btn text class="mx-2" @click="()=>{this.$router.go(-1)}">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <h1>{{plural}}</h1>
          <v-btn class="ml-2" color="info" small fab @click="showCreateMenu"><v-icon>mdi-plus</v-icon></v-btn>
        </span>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="5" sm="4">
        <v-data-table
            :headers="headers"
            :items="allData"
            :options.sync="options"
            :server-items-length="pagination.dataCount"
            :loading="pagination.loading"
            :items-per-page="15"
            class="elevation-1"
            :footer-props="{
              itemsPerPageOptions: pagination.pageSizeOptions
            }"
            @click:row="rowClick"
        >
          <template v-slot:top>
            <v-text-field
                v-model="itemQuerySearch"
                label="Search..."
                class="mx-4"
                clearable
                @input="searchItems"
            ></v-text-field>
          </template>
        </v-data-table>
      </v-col>
      <v-col v-if="showAddNew">
        <v-card outlined>
          <v-card-title>
            <span>Add New {{singular}}</span>
          </v-card-title>
          <v-card-text>
            <v-form v-model="validCreate">
              <v-text-field
                  label="First Name"
                  outlined
                  v-model="formData.firstName"
                  :rules="rules.required"
                  @input="existingData=null"
              />
              <v-text-field
                  label="Last Name"
                  outlined
                  v-model="formData.lastName"
                  :rules="rules.required"
                  @input="existingData=null"
              />
              <v-text-field
                  label="Phone"
                  outlined
                  v-model="formData.phone"
                  :rules="rules.required"
                  @input="existingData=null"
              />
              <v-text-field
                  label="Email"
                  outlined
                  v-model="formData.email"
                  @input="existingData=null"
              />
              <span>
                <h3>Address</h3>
                <v-text-field
                    label="Address Line 1"
                    outlined
                    v-model="formData.address.line1"
                    @input="existingData=null"
                />
                <v-text-field
                    label="Address Line 2"
                    outlined
                    v-model="formData.address.line2"
                    @input="existingData=null"
                />
                <v-text-field
                    label="City"
                    outlined
                    v-model="formData.address.city"
                    @input="existingData=null"
                />
                <v-text-field
                    label="Notes"
                    outlined
                    v-model="formData.notes"
                    @input="existingData=null"
                />
              </span>
            </v-form>
            <div v-if="existingData">
              <p class="red--text">A Customer with this phone number already exists. Are you sure you want to create another?</p>
              <span class="blue--text">{{existingData.firstName}} {{existingData.lastName}}</span>
            </div>
          </v-card-text>
          <v-card-actions>
            <v-btn @click="closeMenu" color="error">Close</v-btn>
            <v-btn color="success" v-if="!existingData" :disabled="!validCreate" @click="createCustomer" :loading="customerLoading">Create</v-btn>
            <v-btn v-else color="success" :disabled="!validCreate" @click="confirmCreateCustomer" :loading="customerLoading">Create New</v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
      <v-col v-if="showSelected && selectedRow">
        <v-card outlined class="mb-2">
          <v-card-title class="d-flex flex-row">
            <span>Showing: {{singular}} #{{selectedRow.id}} ({{selectedRow.firstName}} {{selectedRow.lastName}})</span>
            <v-btn v-if="!this.isEditable" @click="isEditable=true" class="ml-2" fab x-small color="info"><v-icon>mdi-pencil</v-icon></v-btn>
            <v-btn v-if="this.isEditable" @click="isEditable=false" class="ml-2" fab x-small color="error"><v-icon>mdi-close</v-icon></v-btn>
          </v-card-title>
          <span v-if="isEditable">
            <v-card-text>
              <v-form v-model="validCreate">
                <v-text-field
                    label="First Name"
                    outlined
                    v-model="selectedRow.firstName"
                    :rules="rules.required"
                />
                <v-text-field
                    label="Last Name"
                    outlined
                    v-model="selectedRow.lastName"
                />
                <v-text-field
                    label="Phone"
                    outlined
                    v-model="selectedRow.phone"
                />
                <v-text-field
                    label="Email"
                    outlined
                    v-model="selectedRow.email"
                />
                <span>
                  <h3>Address</h3>
                  <v-text-field
                      label="Address Line 1"
                      outlined
                      v-model="selectedRow.address.line1"
                  />
                  <v-text-field
                      label="Address Line 2"
                      outlined
                      v-model="selectedRow.address.line2"
                  />
                  <v-text-field
                      label="City"
                      outlined
                      v-model="selectedRow.address.city"
                  />
                  <v-text-field
                      label="Notes"
                      outlined
                      v-model="selectedRow.notes"
                  />
                </span>
              </v-form>
            </v-card-text>
            <v-card-actions>
              <v-btn @click="saveChanges" color="success">Save Changes</v-btn>
            </v-card-actions>
          </span>
        </v-card>
        <v-card outlined class="mb-2">
          <v-card-title>Appointments</v-card-title>
          <v-data-table
              :headers="appointmentsTableHeaders"
              :items="appointmentsTableItems"
              :items-per-page="-1"
              class="elevation-1"
              @click:row="viewAppointmentRow"
          >
            <template v-slot:item.dealtWithBy="{ item }">
              <span>{{lookupUsername(item.dealtWithBy || item.createdBy)}}</span>
            </template>
            <template v-slot:item.appointmentType="{ item }">
              <span>{{appointmentTypes.find(x => x.id===item.appointmentType)?appointmentTypes.find(x => x.id===item.appointmentType).name:'Unknown'}}</span>
            </template>
            <template v-slot:item.outcome="{ item }">
              <span>{{outcomes.find(x => x.id===item.outcome)?outcomes.find(x => x.id===item.outcome).name:'Unknown'}}</span>
            </template>
            <template v-slot:item.status="{ item }">
              <v-chip :color="utils.getAppointmentStatusColor(item.status)">{{utils.parseAppointmentStatus(item.status)}}</v-chip>
            </template>
            <template v-slot:item.scheduledFor="{ item }">
              <span>{{utils.customFormatDate(item.scheduledFor)}}</span>
            </template>
          </v-data-table>
        </v-card>
        <v-card outlined>
          <v-card-title>Jobs</v-card-title>
          <v-data-table
              outlined
              :headers="jobsTableHeaders"
              :items="jobsTableItems"
              :items-per-page="-1"
              class="elevation-1"
              :loading="jobsTableLoader"
              @click:row="viewJobRow"
          >
            <template v-slot:item.status="{ item }">
              <span>{{ parseJobStatus(item.status) }}</span>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>
    <dialog-snack ref="snackbar" :color="snackObj.color" :text="snackObj.text"/>
  </v-container>
</template>
<script>
  import axios from 'axios';
  import {mapGetters} from "vuex";
  import moment from "moment";
  import utils from "../../plugins/helpers"
  import _ from "lodash";
  export default {
    data () {
      return {
        utils: utils,

        isEditable: false,

        snackObj: {
          color: '',
          text: ''
        },

        jobsTableLoader: false,
        appointmentsTableLoader: false,

        rules: {
          required: [v => !!v || 'This field is required.'],
        },
        validCreate: false,

        itemQuerySearch: '',
        pagination: {
          loading: false,
          dataCount: 0,
          pageSizeOptions: [5, 10, 15, 30, -1]
        },
        options: {
          search: "",
          sortBy: ['firstName'],
          sortDesc: [true],
          page: 1,
          itemsPerPage: 15
        },
        updatingRoute: false,
        init: false,
        manualRoute: false,

        loader: false,
        headers: [
          { text: 'First Name', align: 'start', sortable: true, value: 'firstName' },
          { text: 'Last Name', sortable: true, value: 'lastName' },
          { text: 'Phone', value: 'phone' },
          { text: 'Email', value: 'email' }
        ],
        allData: [],
        formData: {address: {}},

        jobsTableHeaders: [
          { text: 'Status', value: 'status' },
          { text: 'ID', align: 'start', sortable: true, value: 'id' },
          { text: 'Item', sortable: true, value: 'item' },
          { text: 'Date Out', sortable: true, value: 'out' },
          { text: 'Date In', sortable: true, value: 'in' },
          { text: '$C', sortable: true, value: 'totalCost' },
          { text: '$T', sortable: true, value: 'tailorCost' },
          { text: '$R', sortable: true, value: 'revenue' },
        ],
        appointmentsTableHeaders: [
          { text: 'ID', value: 'id' },
          {text: 'Appointment Date', sortable: true, value: 'scheduledFor'},
          {text: 'Appointment Type', sortable: true, value: 'appointmentType'},
          {text: 'Status', sortable: true, value: 'status'},
          {text: 'Outcome', sortable: true, value: 'outcome'},
          {text: 'CSR', align: 'start', sortable: true, value: 'dealtWithBy'},
        ],

        appointmentTypes: [],
        outcomes: [],

        prevRow: null,
        selectedRow: null,
        showAddNew: false,
        showSelected: false,

        existingData: null,
        customerLoading: false,

        jobsTableItems: [],
        appointmentsTableItems: [],

        search: '',

        pluralLower: "customers",
        plural: "Customers",
        singularLower: "customer",
        singular: "Customer"
      }
    },
    async created() {
      try {
        //used for pagination
        let oldQuery = this.getURLQuery();
        this.options.page = oldQuery.page || 1;
        this.options.itemsPerPage = oldQuery.limit || 15
        this.options.sortBy = [oldQuery.sort || "firstName"]
        this.options.sortDesc = [oldQuery.order || true]
        this.options.search = oldQuery.search || ""
        this.itemQuerySearch = this.options.search;
        //used for pagination end
      } catch (error) {
        console.error(error)
        this.snack(error.msg || error, "error");
      }
    },
    async mounted(){
      if(!this.isAllowed('job', 'viewRevenue')){
        let tI = this.jobsTableHeaders.findIndex(x => x.text==="$T");
        if(tI>=0) this.jobsTableHeaders.splice(tI, 1);

        let rI = this.jobsTableHeaders.findIndex(x => x.text==="$R");
        if(rI>=0) this.jobsTableHeaders.splice(tI, 1);
      }

      this.init = true;

      let res = await axios.get(`${this.getEndpoint}/api/appointmentTypes`)
      if(res.data.error) throw res.data.error
      this.appointmentTypes = res.data.data

      res = await axios.get(`${this.getEndpoint}/api/appointmentOutcomes`)
      if(res.data.error) throw res.data.error
      this.outcomes = res.data.data

      this.pagination.pageSizeOptions = this.getGlobalValue("paginationPageLimitsGeneral")?.split(",").map(x => parseInt(x)) || this.pagination.pageSizeOptions;
      await this.getAllData();
      this.init = false;
    },
    watch: {
      options: {
        async handler () {
          if(this.init) return;
          await this.getAllData();
        },
        deep: true,
      },
      $route: {
        handler: async function (current) {
          //used for pagination
          if (this.updatingRoute || this.manualRoute) return;
          let oldQuery = this.getURLQuery(current.query);
          this.options.page = oldQuery.page || 1;
          this.options.itemsPerPage = oldQuery.limit || 15
          this.options.sortBy = [oldQuery.sort || "firstName"]
          this.options.sortDesc = [oldQuery.order || true]
          this.options.search = oldQuery.search || ""
          //used for pagination end include full watcher
          await this.getAllData();
        },
        deep: true
      },
    },
    computed: {
      ...mapGetters(['getEndpoint', "isAllowed", "getGlobalValue", "lookupUsername"])
    },
    methods: {
      searchItems: _.debounce(function(){
        this.options.search = this.itemQuerySearch;
        if(!this.options.search) this.options.search = "" //added in so when clearable is clicked, the table resets properly
        this.getAllData()
      }, 700),
      getURLQuery(custom=null){
        let oldQuery = custom? {...custom}: {...this.$route.query};
        if(oldQuery.limit) oldQuery.limit = parseInt(oldQuery.limit);
        if(oldQuery.page) oldQuery.page = parseInt(oldQuery.page);
        if(oldQuery.order) oldQuery.order = oldQuery.order==='true';
        if(oldQuery.selected) oldQuery.selected = parseInt(oldQuery.selected);
        return oldQuery;
      },
      async rowClick(item, row){
        try{
          if(this.prevRow){
            this.prevRow.select(false);
          }
          this.prevRow = row;
          window.scrollTo(0, 0);
          this.pagination.loading = true

          if(row) row.select(true);
          this.selectedRow = item;


          this.manualRoute = true;
          if(!(this.$route.query.selected && this.$route.query.selected==item.id)){
            await this.$router.replace({path: `/${this.pluralLower}`, query: {...this.$route.query, selected: item.id}})
          }
          this.manualRoute = false;

          await this.loadJobTableData()
          await this.loadAppointmentTableData()
          this.showAddNew = false;
          this.showSelected = true;
        }
        catch (error) {
          console.error(error);
          this.snack(error.msg || error, "error");
        }
        finally {
          this.pagination.loading = false
        }
      },
      viewRow(){
        this.$router.push(`/${this.pluralLower}/view/${this.selectedRow.id}`)
      },
      snack(text, color=""){
        this.snackObj.text = text;
        this.snackObj.color = color;

        this.$refs.snackbar.openDialog();
      },
      async closeMenu(){
        this.showSelected = true;
        this.showAddNew = false;
        await this.$router.go(-1)
      },
      async showCreateMenu(){
        if(!(this.$route.query.create)){
          await this.$router.push({path: `/${this.pluralLower}`, query: {create: "true"}})
        }
        this.formData = {address:{}};
        this.showSelected = false;
        this.showAddNew = true;
      },
      viewJobRow(row){
        this.$router.push(`/jobs?selected=${row.id}`)
      },
      viewAppointmentRow(row){
        this.$router.push(`/appointments/view/${row.id}`)
      },
      async createCustomer(){
        try{
          this.customerLoading = true;

          let res = await axios.post(`${this.getEndpoint}/api/customers`, this.formData)
          if(res.data.error) throw res.data.error;

          let newC = res.data.data;

          if(newC.existing){
            this.existingData = newC;
            this.snack("Customer with this phone number already exists.")
            return;
          }

          this.formData = {address: {}};
          await this.getAllData(res.data.data.id);
          this.snack("Customer Created!", "success")
        }
        catch (error) {
          console.error(error);
          this.snack(error.msg || error, "error");
        }
        finally {
          this.customerLoading = false;
        }
      },
      async confirmCreateCustomer(){
        try{
          this.customerLoading = true;

          let res = await axios.post(`${this.getEndpoint}/api/customers/force`, this.newCustomer)
          if(res.data.error) throw res.data.error;

          await this.getAllData(res.data.data.id);

          this.formData = {address: {}};
          this.snack("Customer Created!", "success")
        }
        catch (error) {
          console.error(error);
          this.snack(error.msg || error, "error");
        }
        finally {
          this.customerLoading = false;
        }
      },
      async getAllData(id=null){
        try {
          //used for pagination
          this.pagination.loading = true;
          let paginationData = {
            page: this.options.page,
            limit: this.options.itemsPerPage,
            sort: (this.options.sortBy.length>0)?this.options.sortBy[0]:"id",
            order: this.options.sortDesc[0],
            search: this.options.search
          }

          let uriFields = Object.keys(paginationData).map(x => {
            return x + "=" + (paginationData[x]!==undefined?encodeURIComponent(paginationData[x]):'')
          }).join("&");
          //used for pagination end

          if(!id && this.$route.query && this.$route.query.selected){
            id = parseInt(this.$route.query.selected);
          }

          let res = await axios.get(`${this.getEndpoint}/api/${this.pluralLower}/tableDataPaginated?${uriFields}`)
          if(res.data.error) throw res.data.error;
          this.allData = res.data.data
          //used for pagination
          this.pagination.dataCount = res.data.total

          if(id){
            paginationData.selected = id;

            res = await axios.get(`${this.getEndpoint}/api/customers/${id}`)
            if(res.data.error) throw res.data.error;

            await this.rowClick(res.data.data)
          }

          let shouldUpdate = false;
          let oldQuery = this.getURLQuery();
          let newQuery = {...paginationData};

          if(Object.keys(newQuery).length!==Object.keys(oldQuery).length) shouldUpdate = true;
          if(!shouldUpdate){
            for(let key of Object.keys(newQuery)){
              if(shouldUpdate) break;

              if(Array.isArray(newQuery[key])){
                if(newQuery[key].length!==oldQuery[key].length){
                  shouldUpdate = true;
                  break;
                }
                for(let i=0;i<newQuery[key].length-1;i++){
                  if(newQuery[key][i]!==oldQuery[key][i]){
                    shouldUpdate = true;
                    break;
                  }
                }
              }
              else if(newQuery[key]!==oldQuery[key]){
                shouldUpdate = true;
              }
            }
          }

          if(shouldUpdate){
            this.updatingRoute = true;
            if(this.init){
              if(this.$route.path===`/${this.pluralLower}`) await this.$router.replace({path: `/${this.pluralLower}`, query: {...this.$route.query, ...newQuery}});
            }
            else{
              if(this.$route.path===`/${this.pluralLower}`) await this.$router.push({path: `/${this.pluralLower}`, query: {...this.$route.query, ...newQuery}});
            }
            this.updatingRoute = false;
          }
          //used for pagination end

          await this.loadJobTableData()
          await this.loadAppointmentTableData()
        } catch (error) {
          console.error(error);
          this.snack(error.msg || error, "error");
        } finally {
          this.pagination.loading = false;
        }
      },
      async loadJobTableData(){
        try {
          if(!this.selectedRow) return
          this.jobsTableLoader = true
          let customerData = await axios.get(`${this.getEndpoint}/api/jobs/customer/${this.selectedRow.id}`)
          this.jobsTableItems = customerData.data.data

          this.jobsTableItems.forEach(x=>{
            x.item = x.Item.Asset.styleNo +(x.Item.colour?" ("+x.Item.colour+")":'')
            x.in = this.momentDateFormat(x.dateIn)
            x.out = this.momentDateFormat(x.dateOut)
            x.hand = this.momentDateFormat(x.handedOffAt)
            x.revenue = x.totalCost-x.tailorCost
          })
        } catch (error) {
          this.snack(error)
          console.log(error)
        } finally {
          this.jobsTableLoader = false
        }
      },
      async loadAppointmentTableData(){
        try {
          if(!this.selectedRow) return
          this.appointmentsTableLoader = true
          let customerData = await axios.get(`${this.getEndpoint}/api/appointments/customer/${this.selectedRow.id}`)
          this.appointmentsTableItems = customerData.data.data
        } catch (error) {
          this.snack(error)
          console.log(error)
        } finally {
          this.appointmentsTableLoader = false
        }
      },
      parseDate(date){
        if(!date) return null;
        return moment(this.parseISODate(date)).format("DD/MM/YYYY")
      },
      momentDateFormat (date) {
        return date ? moment(this.parseISODate(date)).format('ddd, MMM Do YYYY') : '';
      },
      parseISODate(date){
        return (date?new Date(date).toISOString().substring(0, 10):null);
      },
      async saveChanges(){
        try {
          let obj = {
            ...this.selectedRow
          }
          let res = await axios.put(`${this.getEndpoint}/api/${this.pluralLower}/${this.selectedRow.id}`, obj)
          if(res.data.error) throw res.data.error;
          this.snack("Success!")
        } catch (error) {
          this.snack(error)
        }
      },
      parseJobStatus(id){
        if(id===-1) return "Voided"
        else if(id===0) return "New"
        else if(id===1) return "Sent"
        else if(id===2) return "Received"
        else if(id===3) return "Completed"
      },
    }
  }
</script>
